import React, { ReactNode, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Icon } from '@scale/scaleui';
import { Text, Flex } from '@scale/scaleui-radix';
import { motion } from 'framer-motion';
import { OutlineStep } from './types';
import { OutlineSectionStep } from 'react-scale/remotasks.com/components/Expert/OutlinePanel/OutlineSectionStep';

type OutlineSectionProps = {
  title: string;
  courseDuration?: number;
  titleEndAdornment?: ReactNode;
  active?: boolean;
  muted?: boolean;
  steps: OutlineStep[];
  loading?: boolean;
  onHeaderClick?: () => void;
  onStepClick?: () => void;
  setCurrentSectionIndex?: (index: number) => void;
  sectionCount?: number;
};

export const OutlineSectionV2 = ({
  title,
  courseDuration,
  titleEndAdornment,
  steps,
  active,
  muted,
  loading,
  onHeaderClick,
  setCurrentSectionIndex,
  sectionCount,
}: OutlineSectionProps) => {
  const [expanded, setExpanded] = useState(active);
  const handleExpand = () => {
    setExpanded(prev => !prev);
  };

  useEffect(() => {
    setExpanded(active);
  }, [active]);
  const handleStepClick = (index: number) => {
    onHeaderClick && onHeaderClick();
    setCurrentSectionIndex && setCurrentSectionIndex(index);
  };
  return (
    <div
      className={clsx([
        'border-2 rounded w-[266px]',
        active ? 'border-neutral-8 bg-accent-3' : 'border-neutral-6',
        muted ? 'opacity-70' : '',
      ])}
    >
      <div
        className={clsx([
          'p-2 grid grid-flow-col items-center gap-1 grid-cols-[auto_1fr_auto]',
          !expanded && 'rounded',
          expanded && 'border-b-2 border-b-neutral-200 rounded-t',
          onHeaderClick !== undefined && 'cursor-pointer',
        ])}
        onClick={onHeaderClick}
      >
        {steps.length > 0 ? (
          <Icon
            className="cursor-pointer"
            icon={expanded ? 'chevron-up' : 'chevron-down'}
            size="sm"
            onClick={handleExpand}
          />
        ) : (
          <span />
        )}
        <span className="font-sm text-xs text-neutral-700">
          <div className="flex flex-col">
            <Text size="2" weight="medium">
              {title}
            </Text>
            {loading && <Icon icon="spinner" size="sm" />}
            <Flex direction="row" gap="1">
              {sectionCount && (
                <Text size="1" weight="light">
                  {sectionCount} sections
                </Text>
              )}
              {sectionCount && !!courseDuration && (
                <Text size="1" weight="light">
                  •
                </Text>
              )}
              {!!courseDuration && (
                <Text size="1" weight="light">
                  {courseDuration} min
                </Text>
              )}
            </Flex>
          </div>
        </span>
        {titleEndAdornment}
      </div>
      {expanded && (
        <motion.div
          className="bg-neutral-0 w-[100%] rounded-b"
          initial={{ y: -20 }}
          animate={{ y: 0 }}
          transition={{
            ease: 'easeOut',
            duration: 0.15,
          }}
        >
          {steps.map((step, index) => (
            <OutlineSectionStep key={index} step={step} onClick={() => handleStepClick(index)} />
          ))}
        </motion.div>
      )}
    </div>
  );
};
